/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from '@/shared/state/LocalZustand';

/** One push event delivered over the Social real-time WebSocket -- see
 * `broadcast_social_update` on the backend (social_connections_service.py). */
export interface SocialUpdateEvent {
  type: string;
  agency_id: string;
  client_id: string;
  /** Client-assigned receipt time -- makes every event a distinct object even
   * when the same client_id/agency_id repeats, so effects keyed on this value
   * always re-fire instead of being deduped by referential equality. */
  receivedAt: number;
}

interface SocialUpdatesState {
  /** The most recently received push event, or null before the first one. */
  lastUpdate: SocialUpdateEvent | null;
  /** Records a newly received event as the latest one. */
  receiveUpdate: (event: Omit<SocialUpdateEvent, 'receivedAt'>) => void;
}

/**
 * Shared pub/sub bus for the Social WebSocket: `useSocialWebSocket` is the
 * only writer (one connection per agency dashboard, mounted in
 * AgencyDashboardLayout), and any component that wants to react to a live
 * update -- the open chat panel, the client list -- reads `lastUpdate` here
 * instead of each owning its own WebSocket connection.
 */
export const socialUpdatesContext = createLocalZustand<SocialUpdatesState>(
  () => (set) => ({
    lastUpdate: null,
    receiveUpdate: (event) => set({ lastUpdate: { ...event, receivedAt: Date.now() } }),
  }),
);

export const { useStore: useSocialUpdates } = socialUpdatesContext;
